<template>
  <div class="mt-3">
    <!-- ----------------------------------------------------- -->
    <!-- main headings -->
    <!-- ----------------------------------------------------- -->
    <div class="mt-3">
      <div class="row">
        <p class="bb-sheet-title">ABILITIES</p>
      </div>
    </div>
    <div
      v-if="isReady"
      class="container-fluid mt-3 mb-0 bb-tooltip-wrapper"
    >
      <!-- ----------------------------------------------------- -->
      <!-- table headings -->
      <!-- ----------------------------------------------------- -->
      <div class="row">
        <div class="col-1 text-center">
          <p class="bb-hdg mt-2 mb-3">Current</p>
        </div>
        <div class="bb-col-wider text-center">
          <p class="bb-hdg mt-2 mb-3">Base</p>
        </div>
        <div class="col-1 text-center">
          <p class="bb-hdg mt-2 mb-3">Adjs</p>
        </div>
        <div class="col-3 text-center">
          <p class="bb-hdg mt-2 mb-3">Adjustment Reasons</p>
        </div>
        <div class="col-auto ms-3">
          <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
        </div>
      </div>
      <!-- ----------------------------------------------------- -->
      <!-- data rows -->
      <!-- ----------------------------------------------------- -->
      <div
        class="row mt-3 mb-3 align-items-center bb-tooltip-wrapper"
        v-for="abil in filteredAbilities"
        :key="abil.name"
      >
        <!-- ----------------------------------------------------- -->
        <!-- Current -->
        <!-- ----------------------------------------------------- -->
        <div class="col-1 text-center">
          <component
            style="display: block"
            :is="computedCur(abil) ? 'ToolTip' : 'div'"
            :position="'top'"
          >
            <!-- slot content for ToolTip -->
            <template
              v-if="computedCur(abil)"
              #content
            >
              <div>{{ computedCur(abil) }}</div>
            </template>

            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="
                    formatAbilityValue(
                      abil,
                      displayCharacter?.[`${abil.name}_cur`],
                    )
                  "
                  :id="`${abil.name}_cur`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :class="{ 'bb-warning': isCurMax(abil) }"
                  :for="`${abil.name}_cur`"
                >
                  {{ abil.label }}
                  <span
                    v-if="computedCur(abil)"
                    class="bb-orange-circle"
                  ></span>
                </label>
              </div>
            </div>
          </component>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Base -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-wider text-center">
          <component
            style="display: block"
            :is="computedBase(abil) ? 'ToolTip' : 'div'"
            :position="'top'"
          >
            <!-- slot content for ToolTip -->
            <template
              v-if="computedBase(abil)"
              #content
            >
              <div>
                <div>{{ computedBase(abil) }}</div>
                <img
                  v-if="abil.name === 'ca'"
                  :src="bbTableCombatActions"
                  alt="CA Table"
                />
                <img
                  v-if="abil.name === 'damage_mod_melee'"
                  :src="bbTableDamageMods"
                  alt="Damage Mods Table"
                />
              </div>
            </template>

            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="
                    formatAbilityValue(
                      abil,
                      displayCharacter?.[`${abil.name}_base`],
                    )
                  "
                  :id="`${abil.name}_base`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :for="`${abil.name}_base`"
                >
                  {{ abil.label }}
                  <span
                    v-if="computedBase(abil)"
                    class="bb-orange-circle"
                  ></span>
                </label>
              </div>
            </div>
          </component>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adj -->
        <!-- ----------------------------------------------------- -->
        <div class="col-1 text-center">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="adjsTotDisplay(abil)"
                :id="`${abil.name}_adjs_tot`"
                disabled
              />
              <label
                class="readonly bb-float-label optional"
                :for="`${abil.name}_adjs_tot`"
              >
                {{ abil.label }}
              </label>
            </div>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adjustment Reason -->
        <!-- ----------------------------------------------------- -->
        <div class="col-4 text-center">
          <component
            style="display: block"
            :is="computedReasons(abil.name) ? 'ToolTip' : 'div'"
            v-bind="
              computedReasons(abil.name)
                ? {
                    text: displayCharacter?.[`${abil.name}_adjs_reasons`],
                    position: 'top',
                  }
                : {}
            "
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="displayCharacter?.[`${abil.name}_adjs_reasons`]"
                  :id="`${abil.name}_adjs_tot`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :for="`${abil.name}_adjs_reasons`"
                >
                  {{ abil.label }} Adj Reasons
                  <span
                    v-if="computedReasons(abil.name)"
                    class="bb-orange-circle"
                  ></span>
                </label>
              </div>
            </div>
          </component>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adjustment Button -->
        <!-- ----------------------------------------------------- -->
        <div class="col-auto ms-2">
          <button
            :id="abil"
            type="button"
            class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
            @click="goOpenAdjs(abil)"
          >
            <ToolTip
              :text="`${abil.label} Adjustments`"
              position="left"
            >
              <img
                :src="adjustImg"
                height="48"
                width="48"
                alt="adjust image"
              />
            </ToolTip>
          </button>
        </div>
      </div>
      <!-- ------------------------------------------------------------>
      <!-- combat options -->
      <!-- -------------- -->
      <div class="row mt-3 mb-0 ms-3 me-3">
        <div class="row mb-2"></div>
        <p class="bb-hdg mt-2 mb-3">Default Combat Options</p>
        <div class="row mb-2"></div>
        <div class="col-2 text-center me-4">
          <!-- -------------------- -->
          <!--   combat inclusion   -->
          <!-- -------------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`combat-inclusion-${local_character.combat_inclusion}`"
              :class="{ 'bb-readonly-toggle': shouldUseReadonlyAutoInclusion }"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`Auto Inclusion`"
              :labelOff="`Manual Inclusion`"
              :styleOff="`default`"
              :styleOn="shouldDisableCombatInclusion ? 'default' : 'primary'"
              :checked="local_character.combat_inclusion"
              :disabled="shouldDisableCombatInclusion"
              @mouseup="onToggleClick('combat_inclusion')"
              @click.stop="onToggleClick('combat_inclusion')"
            >
            </enhanced-toggle>
          </div>
          <!-- -------------- -->
          <!--    ranged      -->
          <!-- -------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`ranged-${local_character.ranged}`"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`Ranged`"
              :labelOff="`No Ranged`"
              :styleOff="`default`"
              :styleOn="`primary`"
              :checked="local_character.ranged"
              @mouseup="onToggleClick('ranged')"
              @click.stop="onToggleClick('ranged')"
            >
            </enhanced-toggle>
          </div>
        </div>
        <div class="col-2 text-center me-4 ms-4">
          <!-- -------------------------------------- -->
          <!--   shield [need key for reactivity]     -->
          <!-- -------------------------------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`shield-${local_character.shield}`"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`Shield`"
              :labelOff="`No Shield`"
              :styleOff="`default`"
              :styleOn="`primary`"
              :checked="local_character.shield"
              @mouseup="onToggleClick('shield')"
              @click.stop="onToggleClick('shield')"
            >
            </enhanced-toggle>
          </div>
          <!-- -------------------------------------- -->
          <!--   2nd Weapon [need key for reactivity] -->
          <!-- -------------------------------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`second-weapon-${local_character.second_weapon}`"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`2nd Weapon`"
              :labelOff="`Single Weapon`"
              :styleOff="`default`"
              :styleOn="`primary`"
              :checked="local_character.second_weapon"
              @mouseup="onToggleClick('second_weapon')"
              @click.stop="onToggleClick('second_weapon')"
            >
            </enhanced-toggle>
          </div>
        </div>
        <div class="col-2 text-center me-4 ms-4">
          <!-- ---------------- -->
          <!--  special action  -->
          <!-- ---------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`special-action-${local_character.special_action}`"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`Special Action`"
              :labelOff="`Normal Actions`"
              :styleOff="`default`"
              :styleOn="`primary`"
              :checked="local_character.special_action"
              @mouseup="onToggleClick('special_action')"
              @click.stop="onToggleClick('special_action')"
            >
            </enhanced-toggle>
          </div>
          <!-- ----------------- -->
          <!-- special reaction  -->
          <!-- ----------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`special-reaction-${local_character.special_reaction}`"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`Special Reaction`"
              :labelOff="`Normal Reactions`"
              :styleOff="`default`"
              :styleOn="`primary`"
              :checked="local_character.special_reaction"
              @mouseup="onToggleClick('special_reaction')"
              @click.stop="onToggleClick('special_reaction')"
            >
            </enhanced-toggle>
          </div>
        </div>
        <div class="col-2 text-center ms-4">
          <!-- -------------- -->
          <!--   magic-user   -->
          <!-- -------------- -->
          <div class="row">
            <enhanced-toggle
              :key="`magic-${local_character.magic}`"
              style="
                --toggleWidth: 380px;
                --labelWidth: 240px;
                --switchWidth: 60px;
                --switchPos: 275px;
                height: 50px;
                border-radius: 10px;
                background-color: white;
              "
              :labelOn="`Magic`"
              :labelOff="`No Magic`"
              :styleOff="`default`"
              :styleOn="`primary`"
              :checked="local_character.magic"
              @mouseup="onToggleClick('magic')"
              @click.stop="onToggleClick('magic')"
            >
            </enhanced-toggle>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'

import CounterComponent from './Counter.vue'
import ToolTip from '../components/ToolTip.vue'

import bbTableCombatActions from '../assets/images/bbTableCombatActions.png'
import bbTableDamageMods from '../assets/images/bbTableDamageMods.png'
import adjustImg from '../assets/images/bbAdjust.png'
import buildDisplayedAbilitySnapshot from '../helpers/buildDisplayedAbilitySnapshot'
import copyObjectNoRef from '@/helpers/copyObjectNoRef'

export default {
  name: 'CharacterSheetCharacteristics',
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    listScope: {
      type: String,
      default: 'campaign',
    },
  },
  //========================================================
  // components
  //------------
  components: {
    CounterComponent,
    ToolTip,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // watch
  //-------
  watch: {
    character: {
      deep: true,
      immediate: true,
      handler(newVal) {
        console.log('=====================================')
        console.log('WATCH CHARACTER SHEET ABILITIES...', newVal)
        if (!newVal) return
        this.local_character = copyObjectNoRef(newVal)

        if (this.shouldForceCombatInclusionOff(this.local_character)) {
          this.local_character.combat_inclusion = false
        }
      },
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      bbTableCombatActions,
      bbTableDamageMods,
      adjustImg,
      //
      local_character: null,
      //
      isInitialLoad: true,
      //
      abils: [
        {
          name: 'ca',
          label: 'CA',
          full_name: 'Combat Actions',
        },
        {
          name: 'cr',
          label: 'CR',
          full_name: 'Combat Reactions',
        },
        {
          name: 'sr',
          label: 'SR',
          full_name: 'Strike Rank',
        },
        {
          name: 'damage_mod_melee',
          label: 'Dam Melee',
          full_name: 'Melee Damage',
        },
        {
          name: 'damage_mod_ranged',
          label: 'Dam Ranged',
          full_name: 'Ranged Damage',
        },
        {
          name: 'move',
          label: 'Move',
          full_name: 'Movement',
        },
        {
          name: 'move_fly',
          label: 'Fly',
          full_name: 'Fly',
        },
      ],
    }
  },
  //========================================================
  // computed
  //-----------
  computed: {
    isReady() {
      if (this.character != null) {
        return true
      }
      return false
    },
    isMyCharactersScope() {
      return this.listScope === 'mine'
    },
    shouldUseReadonlyAutoInclusion() {
      return this.isMyCharactersScope
    },
    shouldDisableCombatInclusion() {
      return (
        this.shouldUseReadonlyAutoInclusion ||
        this.shouldForceCombatInclusionOff(this.local_character)
      )
    },
    filteredAbilities() {
      return this.abils.filter(
        (abil) =>
          abil.name !== 'move_fly' || this.displayCharacter?.move_fly_base > 0,
      )
    },
    displayCharacter() {
      return buildDisplayedAbilitySnapshot(
        this.local_character || this.character,
      )
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    shouldForceCombatInclusionOff(character) {
      const status = character?.status
      return status === 'Dead' || status === 'Retired'
    },
    formatMoveValue(value) {
      const numericValue = Number(value)

      if (Number.isNaN(numericValue)) {
        return value
      }

      return Number.isInteger(numericValue)
        ? numericValue
        : numericValue.toFixed(1)
    },
    formatAbilityValue(abil, value) {
      if (abil?.name === 'move' || abil?.name === 'move_fly') {
        return this.formatMoveValue(value)
      }

      const formatSignedValue = (rawValue) => {
        if (
          typeof rawValue === 'string' &&
          /^[+-]\d+(?:\.\d+)?$/.test(rawValue.trim())
        ) {
          return rawValue.trim()
        }

        const numericValue = Number(rawValue)

        if (Number.isNaN(numericValue)) {
          return rawValue
        }

        return numericValue > 0 ? `+${numericValue}` : `${numericValue}`
      }

      if (abil.name == 'damage_mod_melee') {
        return formatSignedValue(value)
      } else if (abil.name == 'damage_mod_ranged') {
        return formatSignedValue(value)
      }
      return value
    },
    computedCur(abil) {
      if (!this.displayCharacter || !abil || typeof abil !== 'object')
        return false

      return `Base (${this.displayCharacter[`${abil.name}_base`]}) + Adjs (${this.displayCharacter[`${abil.name}_adjs_tot`]})`
    },
    computedBase(abil) {
      if (!this.displayCharacter || !abil || typeof abil !== 'object')
        return false

      if (abil.name == 'ca') {
        return `Dex (${this.displayCharacter.dex_cur})`
      } else if (abil.name == 'sr') {
        return `Int (${this.displayCharacter.int_cur}) + Dex (${this.displayCharacter.dex_cur}) / 2`
      } else if (abil.name == 'damage_mod_melee') {
        return `Str (${this.displayCharacter.str_cur}) + Siz (${this.displayCharacter.siz_cur})`
      }
      return null
    },
    isCurMax(abil) {
      if (abil.name == 'ca' || abil.name == 'cr') {
        return (
          this.displayCharacter?.[`${abil.name}_cur`] >=
          this.config[`max_${abil.name}`]
        )
      }
      return false
    },
    getAbilFullName(abil) {
      const found = this.abils.find((a) => a.name === abil.name)
      return found ? found.full_name : abil
    },
    adjsTotDisplay(abil) {
      if (!this.displayCharacter) return ''
      const val = this.displayCharacter[`${abil.name}_adjs_tot`] ?? 0
      const displayValue = this.formatAbilityValue(abil, val)
      // return val > 0 ? `+${displayValue}` : `${displayValue}`
      return displayValue
    },
    computedReasons(abil_name) {
      if (!this.displayCharacter || !abil_name || typeof abil_name !== 'string')
        return false
      const val = this.displayCharacter[`${abil_name}_adjs_reasons`]
      return typeof val === 'string' && val.trim() !== ''
    },
    getTextSizeClass(text) {
      console.log('getTextSizeClass called with text:', text)
      if (!text) return 'text-normal'

      const length = text.length
      console.log('Text length:', length)
      if (length > 100) return 'text-xs'
      if (length > 70) return 'text-sm'
      if (length > 40) return 'text-md'
      return 'text-normal'
    },
    goOpenAdjs(abil) {
      this.$emit('adjust', {
        adj_category: 'ability',
        adj_type: abil.name,
        adj_label: this.getAbilFullName(abil),
      })
    },
    //========================================================
    // on toggle click
    //-----------------
    onToggleClick(name) {
      if (name === 'combat_inclusion' && this.shouldDisableCombatInclusion) {
        return
      }
      console.log('TOGGLE CLICKED: ', name)
      this.local_character[`${name}`] = !this.local_character[`${name}`]
      this.emitUpdate(`${name}`, this.local_character[`${name}`])
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      console.log('emitUpdate: ' + key + ' = ' + value)
      this.$emit('update', { type: 'ability', key: key, value: value })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {},
}
</script>

<style scoped>
.form-group {
  margin-bottom: 0;
}

:deep(.bb-readonly-toggle.enhancedCheck-disabled .enhancedToggle-label) {
  background: var(--enhanced-check-color-default) !important;
  color: #fff !important;
}

:deep(.bb-readonly-toggle.enhancedCheck-disabled .enhancedToggle-switch) {
  background: #fff !important;
  border-color: var(--enhanced-check-color-border) !important;
}

:deep(.bb-readonly-toggle.enhancedCheck-disabled) {
  opacity: 1;
  border: 1px solid var(--enhanced-check-color-border);
}
</style>
